import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ActionDropdown,
  ActionDropdownAction,
  Button,
  InputSearch,
  Pagination,
  Status,
  TableData,
  UniversalFilter,
} from '@/components';
import { Column } from '@/types/common';
import {
  GetPackagesParams,
  PackageListItem,
  PackageStatus,
  PackageVisibility,
  PACKAGE_STATUS_LABEL,
  RentType,
  RENT_TYPE_LABEL,
  PACKAGE_IMAGE_PLACEHOLDER,
} from '@/types/package';
import { getPackages } from '@/services/api/packages';
import { showErrorToast } from '@/components/toast/globalToast';
import { extractApiErrorMessage, formatRupiah } from '@/utils';
import CampaignLine from './components/CampaignLine';
import { PackageVehiclesModal } from './components/PackageVehiclesModal';
import { VisibilityTab, visibilityTabs } from './packageDisplay';
import { packageStatusAction, usePackageStatusDialog } from './packageStatus';

interface PackageFilters extends Record<string, any> {
  contractType?: RentType[];
  status?: PackageStatus[];
}

// The API takes a single value per filter, so only the first selection is
// sent when several are ticked.
const filterParams = (
  search: string,
  filters: PackageFilters
): GetPackagesParams => ({
  ...(search.trim() ? { search: search.trim() } : {}),
  ...(filters.contractType?.length
    ? { contractType: filters.contractType[0] }
    : {}),
  ...(filters.status?.length ? { status: filters.status[0] } : {}),
});

const PackageListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [packages, setPackages] = useState<PackageListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [activeFilters, setActiveFilters] = useState<PackageFilters>({});
  const [visibilityTab, setVisibilityTab] = useState<VisibilityTab>('ALL');
  const [counts, setCounts] = useState<
    Record<PackageVisibility, number | null>
  >({ [PackageVisibility.PUBLIC]: null, [PackageVisibility.PRIVATE]: null });
  const [actionDropdownOpen, setActionDropdownOpen] = useState<
    string | number | null
  >(null);
  const [vehiclesFor, setVehiclesFor] = useState<PackageListItem | null>(null);

  const handleToggleDropdown = useCallback((id: string | number) => {
    setActionDropdownOpen((prev) => (prev === id ? null : id));
  }, []);

  useEffect(() => {
    const handleOutsideClick = () => setActionDropdownOpen(null);
    if (actionDropdownOpen !== null) {
      document.addEventListener('click', handleOutsideClick);
    }
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [actionDropdownOpen]);

  const filterConfigs = useMemo(
    () => [
      {
        key: 'contractType',
        title: 'Type',
        icon: 'ri-file-paper-2-line',
        multiple: true,
        staticOptions: Object.values(RentType).map((type) => ({
          key: type.toLowerCase(),
          label: RENT_TYPE_LABEL[type],
          value: type,
        })),
      },
      {
        key: 'status',
        title: 'Status',
        icon: 'ri-checkbox-circle-line',
        multiple: true,
        staticOptions: Object.values(PackageStatus).map((status) => ({
          key: status.toLowerCase(),
          label: PACKAGE_STATUS_LABEL[status],
          value: status,
        })),
      },
    ],
    []
  );

  const fetchPackages = useCallback(
    async (
      page: number,
      search: string,
      filters: PackageFilters,
      size: number,
      visibility: VisibilityTab
    ) => {
      setIsLoading(true);
      try {
        const response = await getPackages({
          page,
          size,
          ...filterParams(search, filters),
          ...(visibility === 'ALL' ? {} : { visibility }),
        });

        setPackages(response?.items || []);
        setTotal(response?.pagination?.total || 0);
      } catch (error) {
        showErrorToast(
          'Error',
          extractApiErrorMessage(error, 'Failed to load packages')
        );
        setPackages([]);
        setTotal(0);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  // Tab counts follow search and filters, so Semua matches the table total.
  const fetchCounts = useCallback(
    async (search: string, filters: PackageFilters) => {
      const total = (visibility: PackageVisibility) =>
        getPackages({ size: 1, visibility, ...filterParams(search, filters) })
          .then((response) => response?.pagination?.total ?? 0)
          .catch(() => null);
      const [publik, khusus] = await Promise.all([
        total(PackageVisibility.PUBLIC),
        total(PackageVisibility.PRIVATE),
      ]);
      setCounts({
        [PackageVisibility.PUBLIC]: publik,
        [PackageVisibility.PRIVATE]: khusus,
      });
    },
    []
  );

  useEffect(() => {
    fetchPackages(
      currentPage,
      searchTerm,
      activeFilters,
      pageSize,
      visibilityTab
    );
  }, [
    fetchPackages,
    currentPage,
    searchTerm,
    activeFilters,
    pageSize,
    visibilityTab,
  ]);

  useEffect(() => {
    fetchCounts(searchTerm, activeFilters);
  }, [fetchCounts, searchTerm, activeFilters]);

  const statusDialog = usePackageStatusDialog(() => {
    fetchPackages(
      currentPage,
      searchTerm,
      activeFilters,
      pageSize,
      visibilityTab
    );
    fetchCounts(searchTerm, activeFilters);
  });

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const columns: Column<PackageListItem>[] = [
    {
      key: 'model',
      title: 'Model',
      render: (_value: unknown, record: PackageListItem) => (
        <span className="text-[14px]">{record.model?.name || '-'}</span>
      ),
    },
    {
      key: 'contractType',
      title: 'Type',
      render: (value: RentType) =>
        value ? (
          <Status value={value} type="rentType" size="small" />
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
    {
      key: 'name',
      title: 'Scheme',
      // Package names are free text now, so there is no tier to map to a chip.
      // The uploaded artwork identifies the package instead, with a
      // placeholder for the ones saved before the upload existed.
      render: (value: string, record: PackageListItem) => (
        <div className="flex items-center gap-2.5">
          <img
            src={record.imageURL || PACKAGE_IMAGE_PLACEHOLDER}
            alt=""
            className={`h-8 w-16 rounded object-cover bg-gray-100 border border-gray-200 flex-shrink-0 ${
              record.imageURL ? '' : 'opacity-40 object-contain'
            }`}
          />
          <span className="text-[14px]">{value || '-'}</span>
        </div>
      ),
    },
    {
      key: 'visibility',
      title: 'Visibilitas',
      // Khusus carries its campaign line here: the column it replaced was a
      // dash on every Publik row, which is most of them.
      render: (value: PackageVisibility, record: PackageListItem) => (
        <div>
          <Status value={value} type="packageVisibility" size="small" />
          <CampaignLine heldBy={record.heldBy} visibility={value} />
        </div>
      ),
    },
    {
      key: 'dailyPrice',
      title: 'Sewa/Hari',
      render: (value: number) => (
        <span className="text-[14px]">
          {formatRupiah(value, '-', { space: false })}
        </span>
      ),
    },
    {
      key: 'activePreOrders',
      title: 'Pre-Order Aktif',
      align: 'center',
      render: (value: number) => (
        <span className="text-[14px]">
          {Number.isFinite(value) ? value : '-'}
        </span>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      render: (value: PackageStatus) => (
        <Status value={value} type="packageStatus" size="medium" />
      ),
    },
    {
      key: 'actions',
      title: '',
      width: 'w-16',
      align: 'center',
      render: (_value: unknown, record: PackageListItem, index: number) => {
        const uniqueId = record.id || `temp-${index}`;
        const actions: ActionDropdownAction[] = [
          {
            key: 'edit',
            label: 'Edit',
            icon: 'ri-pencil-line',
            onClick: () => {
              setActionDropdownOpen(null);
              navigate(`/packages/${record.id}`);
            },
          },
        ];
        actions.push({
          key: 'vehicles',
          label: 'Lihat Unit',
          icon: 'ri-motorbike-line',
          onClick: () => {
            setActionDropdownOpen(null);
            setVehiclesFor(record);
          },
        });
        const statusAction = packageStatusAction(record.status);
        if (statusAction) {
          actions.push({
            key: 'status',
            label: statusAction.label,
            icon: statusAction.icon,
            onClick: () => {
              setActionDropdownOpen(null);
              statusDialog.request(record.id, record.status);
            },
          });
        }

        return (
          <ActionDropdown
            record={record}
            isOpen={actionDropdownOpen === uniqueId}
            onToggle={() => handleToggleDropdown(uniqueId)}
            actions={actions}
            ariaLabel={`Actions for ${record.name} package`}
          />
        );
      },
    },
  ];

  return (
    <div className="bg-white p-6 max-w-full mx-auto">
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-[24px] font-semibold text-gray-900 mb-1">
              Package
            </h1>
            <p className="text-[14px] text-gray-600">
              Track and manage Dash Rent pricing packages.
            </p>
          </div>
          <Button
            variant="primary"
            size="small"
            onClick={() => navigate('/packages/create')}
          >
            <i className="ri-add-line text-[12px] mr-1"></i>
            Create Package
          </Button>
        </div>
      </div>

      <div className="rounded-lg p-2 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <div
            role="tablist"
            aria-label="Visibilitas"
            className="mr-auto inline-flex rounded-lg bg-[#F3F4F6] p-1"
          >
            {visibilityTabs(counts).map((tab) => {
              const selected = visibilityTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => {
                    setVisibilityTab(tab.key);
                    setCurrentPage(1);
                  }}
                  className={`rounded-md px-3 py-1.5 text-[13px] font-medium ${
                    selected
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                  {tab.count !== null && ` · ${tab.count}`}
                </button>
              );
            })}
          </div>

          <InputSearch
            placeholder="Search..."
            value={searchInput}
            onChange={setSearchInput}
            onSearch={handleSearch}
            onClear={() => handleSearch('')}
            debounceMs={300}
            size="small"
          />

          <UniversalFilter
            filters={filterConfigs}
            currentFilters={activeFilters}
            onApply={(filters: PackageFilters) => {
              setActiveFilters(filters);
              setCurrentPage(1);
            }}
            onClear={() => {
              setActiveFilters({});
              setCurrentPage(1);
            }}
          />
        </div>
      </div>

      <div className="relative">
        {isLoading ? (
          <div className="bg-white rounded-lg border border-gray-200 flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-2 text-gray-600">Loading packages...</span>
          </div>
        ) : (
          <TableData
            data={packages}
            columns={columns}
            size="small"
            className="min-w-full"
            showHeaderWhenEmpty
            onRowClick={(record) =>
              record.id && navigate(`/packages/${record.id}`)
            }
            emptyState={
              <div className="py-20">
                <div className="w-[148px] h-[148px] mx-auto mb-4">
                  <img
                    src="/images/no-results.png"
                    alt="No packages yet"
                    className="w-full h-full"
                  />
                </div>
                <h3
                  className="font-['Plus_Jakarta_Sans'] font-medium text-[18px] leading-[24px] text-gray-900 mb-2 text-center"
                  style={{ letterSpacing: '-1.5%' }}
                >
                  No Package to Display Yet
                </h3>
                <p
                  className="font-['Plus_Jakarta_Sans'] font-normal text-[14px] leading-[20px] text-gray-500 text-center max-w-[400px] mx-auto"
                  style={{ letterSpacing: '-0.6%' }}
                >
                  Click + Create Package to add a new Dash Rent pricing package
                  and start renting it out.
                </p>
              </div>
            }
          />
        )}
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={total}
        itemsPerPage={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
          setCurrentPage(1);
        }}
      />

      {statusDialog.dialog}

      {vehiclesFor && (
        <PackageVehiclesModal
          onClose={() => setVehiclesFor(null)}
          source={{ packageID: vehiclesFor.id }}
          packageName={vehiclesFor.name}
        />
      )}
    </div>
  );
};

export default PackageListPage;
